import {
  Button,
  ButtonProps,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FlexProps,
  Skeleton,
  UseDisclosureReturn,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, { useEffect, useMemo, useState } from 'react';

import { finleyAssertUnreachable } from '@finley-technologies/common';

import { EVENT_LOG_DRAWER_DISCLOSURE_SEARCH_PARAM } from './constants';
import { EventFeed } from './EventFeed';
import { EventLogExportButton } from './EventLogExportButton';
import {
  EventLogDateRangeFilter,
  EventLogFiltersProvider,
  EventLogTypeFilter,
  FilterButton,
  FilterContainer,
} from './EventLogFilters';
import {
  EventLogNewThreadEditor,
  EventLogNewThreadEditorProps,
} from './EventLogNewThreadEditor';
import { eventLogTypeToDomain } from './helpers';
import {
  useEventLogQueryFilteredParams,
  useEventLogs,
} from './hooks/useEventLogs';
import { ThreadFeed } from './ThreadFeed';
import {
  EventLogTypeGQLType,
  ThreadCountsGQLType,
  ThreadDomainGQLType,
} from '../../../types/dashboard-core-gqlTypes.generated.d';
import { ErrorBoundary } from '~/contexts/MonitoringProvider';
import * as CreditFacilitiesEntityConfig from '~/core/routes/credit-facilities/EntityConfig';
import * as DeliverablesEntityConfig from '~/core/routes/deliverables/EntityConfig';
import * as FundingRequestsEntityConfig from '~/core/routes/funding-requests/EntityConfig';
import * as InvoiceEntityConfig from '~/core/routes/invoices/EntityConfig';
import * as LimitedPartnersEntityConfig from '~/core/routes/limited-partners/EntityConfig';
import * as UsersEntityConfig from '~/core/routes/users/EntityConfig';
import * as ValleyBankLoanAccountsEntityConfig from '~/core/routes/valley-bank/EntityConfig';
import { useGetThreadCountsByDomainQuery } from '~/packages/comments/queries/GetThreadCountsByDomain.gql.generated';
import { useGetThreadsByDomainIdentifierQuery } from '~/packages/comments/queries/GetThreadsByDomainIdentifier.gql.generated';
import { useGetThreadsByDomainMappingQuery } from '~/packages/comments/queries/GetThreadsByDomainMapping.gql.generated';
import { Counter } from '~/packages/common-frontend/components/Counter';
import { IconButtonV2 } from '~/packages/common-frontend/components/IconButton';
import { Icon } from '~/packages/common-frontend/components/icons';
import { useDelayReveal } from '~/packages/common-frontend/hooks/useDelayReveal';
import { useSearchParamsDisclosure } from '~/packages/common-frontend/hooks/useSearchParamDisclosure';

// Fragments
const TabButton = (props: ButtonProps) => (
  <Button
    variant="primary-muted"
    flexShrink={0}
    size="sm"
    colorScheme="gray"
    {...props}
  />
);

const TabContainer = (props: FlexProps) => (
  <Flex
    direction="column"
    gap="0.75rem"
    flexGrow={1}
    overflowY="auto"
    paddingBlock="1rem"
    paddingInline="1.5rem"
    {...props}
  />
);

type EventLogTabProps = {
  entityIds: string[] | undefined;
  eventLogTypes: EventLogTypeGQLType[];
  onThreadAction?: () => void;
};
const EventLogTabBase = ({
  entityIds,
  eventLogTypes,
  onThreadAction,
}: EventLogTabProps) => {
  const isSingleEntity = entityIds?.length === 1;
  const filteredParams = useEventLogQueryFilteredParams();
  const eventLogsQuery = useEventLogs({
    variables: { params: filteredParams },
  });
  const eventLogs = eventLogsQuery.data?.eventLogs ?? {
    __typename: 'EventLogPaginatedFeed',
    hasMore: false,
    items: [],
  };
  if (eventLogsQuery.error) throw eventLogsQuery.error;
  const logDomains = eventLogTypeToDomain(eventLogTypes);
  return (
    <>
      <FilterContainer paddingInline="1.5rem">
        <EventLogExportButton filterParams={filteredParams} />
        <Divider orientation="vertical" height="auto" />
        <EventLogTypeFilter />
        <EventLogDateRangeFilter />
        <UsersEntityConfig.EntityFilter />
        {(logDomains.includes('deliverable') ||
          logDomains.includes('fundingRequest')) && (
          <CreditFacilitiesEntityConfig.EntityFilter />
        )}
        {!isSingleEntity && (
          <>
            {logDomains.includes('fundingRequest') && (
              <FundingRequestsEntityConfig.EntityFilter />
            )}
            {logDomains.includes('invoice') && (
              <InvoiceEntityConfig.EntityFilter />
            )}
            {logDomains.includes('limitedPartner') && (
              <LimitedPartnersEntityConfig.EntityFilter />
            )}
            {logDomains.includes('deliverable') && (
              <DeliverablesEntityConfig.EntityFilter />
            )}
            {logDomains.includes('valleyBank_loanAccount') && (
              <ValleyBankLoanAccountsEntityConfig.EntityFilter />
            )}
          </>
        )}
      </FilterContainer>
      <TabContainer>
        <EventFeed
          logs={eventLogs.items}
          hasMore={eventLogs.hasMore}
          fetchMore={(before) =>
            eventLogsQuery.fetchMore({
              variables: { params: { ...filteredParams, before } },
            })
          }
          onThreadAction={onThreadAction}
        />
      </TabContainer>
    </>
  );
};

const EventLogTab = ({
  entityIds,
  eventLogTypes,
  ...rest
}: EventLogTabProps & {
  entityIds: string[] | undefined;
  eventLogTypes: EventLogTypeGQLType[];
}) => {
  const partialInitialValues: React.ComponentProps<
    typeof EventLogFiltersProvider
  >['partialInitialValues'] = { allowedEventTypes: eventLogTypes };

  // !TODO convert to union type
  const logDomains = eventLogTypeToDomain(eventLogTypes);
  if (logDomains.includes('deliverable')) {
    partialInitialValues.deliverableIds = entityIds;
  } else if (logDomains.includes('fundingRequest')) {
    partialInitialValues.fundingRequestIds = entityIds;
  } else if (logDomains.includes('invoice')) {
    partialInitialValues.invoiceIds = entityIds;
  } else if (logDomains.includes('limitedPartner')) {
    partialInitialValues.limitedPartnerIds = entityIds;
  } else if (logDomains.includes('valleyBank')) {
    partialInitialValues.valleyBankLoanAccountIds = entityIds;
  }

  return (
    <EventLogFiltersProvider partialInitialValues={partialInitialValues}>
      <EventLogTabBase
        entityIds={entityIds}
        eventLogTypes={eventLogTypes}
        {...rest}
      />
    </EventLogFiltersProvider>
  );
};

/**
 * Swaps between the single-entity `useGetThreadsByDomainIdentifierQuery` and the multi-entity
 * `useGetThreadsByDomainMappingQuery`, maps data back to common format
 */
const useThreadFeed = (options: {
  domain: ThreadDomainGQLType;
  domainIdentifier: string | null;
  onThreadAction?: () => void;
}) => {
  if (options.domainIdentifier) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const query = useGetThreadsByDomainIdentifierQuery({
      fetchPolicy: 'cache-and-network',
      variables: {
        domain: options.domain,
        domainIdentifier: options.domainIdentifier,
      },
    });
    return { ...query, data: query.data?.threads };
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const query = useGetThreadsByDomainMappingQuery({
    fetchPolicy: 'cache-and-network',
    variables: { domain: options.domain },
  });
  return {
    ...query,
    data: query.data?.threadsByDomainMapping.map((mapping) => mapping.thread),
  };
};

type ThreadsTabProps = {
  domain: ThreadDomainGQLType;
  domainIdentifier: string | null;
  onThreadAction?: () => void;
  counts?: ThreadCountsGQLType;
};
const ThreadsTab = ({
  domain,
  domainIdentifier,
  onThreadAction,
  counts,
}: ThreadsTabProps) => {
  const [filters, setFilters] = useState<'all' | 'pinned' | 'resolved'>('all');

  const threadsQuery = useThreadFeed({ domain, domainIdentifier });

  const threads = threadsQuery.data ?? [];
  if (threadsQuery.error) throw threadsQuery.error;

  const filteredThreads = [...threads].reverse().filter((thread) => {
    switch (filters) {
      case 'pinned':
        return thread.pinned;
      case 'resolved':
        return thread.resolved;
      case 'all':
        return true;
      default:
        throw finleyAssertUnreachable(filters);
    }
  });

  return (
    <>
      <FilterContainer paddingInline="1.5rem">
        <FilterButton
          isActive={filters === 'all'}
          onClick={() => setFilters('all')}
          rightIcon={
            counts && <Counter size="sm" count={counts.total} maxCount={99} />
          }
        >
          All
        </FilterButton>
        <FilterButton
          isActive={filters === 'pinned'}
          onClick={() => setFilters('pinned')}
          rightIcon={
            counts && <Counter size="sm" count={counts.pinned} maxCount={99} />
          }
        >
          Pinned
        </FilterButton>
        <FilterButton
          isActive={filters === 'resolved'}
          onClick={() => setFilters('resolved')}
          rightIcon={
            counts && (
              <Counter size="sm" count={counts.resolved} maxCount={99} />
            )
          }
        >
          Resolved
        </FilterButton>
      </FilterContainer>

      <TabContainer>
        <ThreadFeed
          threads={filteredThreads}
          onThreadAction={() => {
            onThreadAction?.();
            threadsQuery.refetch();
          }}
        />
      </TabContainer>
    </>
  );
};

type EventLogDrawerProps = {
  entityIds?: string[];
  eventLogTypes: EventLogTypeGQLType[];
  entityThreadDomain: ThreadDomainGQLType;
  triggerButtonProps?: Partial<React.ComponentProps<typeof IconButtonV2>>;
  header?: React.ReactNode;
  disclosure?: UseDisclosureReturn;
  externalOrgOptions?: EventLogNewThreadEditorProps['externalOrgOptions'];
  onCreateThread?: EventLogNewThreadEditorProps['onCreateThread'];
  onThreadAction?: () => void;
};
const EventLogDrawerBase = ({
  triggerButtonProps,
  header = 'Activity',
  disclosure: externalDisclosure,
  entityIds,
  eventLogTypes,
  entityThreadDomain,
  externalOrgOptions,
  onCreateThread,
  onThreadAction,
}: EventLogDrawerProps) => {
  // If we have one entity ID, this is a "single-entity" drawer that has a single domain identifier
  const domainIdentifier = entityIds?.length === 1 ? entityIds[0] : null;

  // Auto-open drawer when `?<EVENT_LOG_DRAWER_DISCLOSURE_SEARCH_PARAM>` search param is set
  const disclosure = useSearchParamsDisclosure({
    key: EVENT_LOG_DRAWER_DISCLOSURE_SEARCH_PARAM,
    disclosure: externalDisclosure,
  });

  const [tab, setTab] = useState<'event-log' | 'threads'>('event-log');

  // Stagger the polling a bit juuuust in case
  const pollInterval = useMemo(() => (10 + Math.random() * 5) * 1000, []);
  const threadCountsQuery = useGetThreadCountsByDomainQuery({
    fetchPolicy: 'cache-and-network',
    pollInterval,
    variables: {
      domain: entityThreadDomain,
      domainIdentifier,
    },
  });

  // Chakra mounts drawer UI when the drawer is opened. In cases where there are a lot of threads
  // with a lot of comments, _a lot_ of DOM nodes have to be created and inserted into the DOM
  // _as the drawer is opening_. On some browsers, the combination of a high DOM node insert count
  // combined with a CSS "slide out" animation causes the UI to lock up briefly. To avoid this, we
  // delay the DOM node insertion until _slightly after_ the slide out animation completes. This
  // lets the browser handle the CSS transition and the DOM insertion separately, so we get very
  // smooth perf.
  const revealed = useDelayReveal({
    skip: !disclosure.isOpen,
    delayBy: 500,
  });

  const counts = threadCountsQuery.data?.threadCountsByDomain;
  const hasDotIndicator = counts ? counts.total - counts.resolved > 0 : false;
  useEffect(() => {
    // Log the error, but don't do anything else. We don't want to keep the drawer from being used
    // because we couldn't fetch thread counts
    if (threadCountsQuery.error) {
      Sentry.captureException(threadCountsQuery.error);
    }
  }, [threadCountsQuery.error]);

  return (
    <>
      <IconButtonV2
        onClick={disclosure.onToggle}
        icon={<Icon icon="bell" />}
        isLoading={!counts}
        hasDotIndicator={hasDotIndicator}
        placement="bottom-end"
        {...triggerButtonProps}
        variant={triggerButtonProps?.variant ?? 'primary-muted'}
        aria-label={triggerButtonProps?.['aria-label'] ?? 'Activity'}
      />
      <Drawer size="lg" placement="right" {...disclosure}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>{header}</DrawerHeader>
          <DrawerBody display="flex" flexDirection="column" padding={0}>
            <FilterContainer paddingInline="1.5rem">
              <TabButton
                isActive={tab === 'event-log'}
                onClick={() => setTab('event-log')}
              >
                All Activity
              </TabButton>
              <TabButton
                isActive={tab === 'threads'}
                onClick={() => setTab('threads')}
                rightIcon={
                  counts && (
                    <Counter
                      size="sm"
                      count={counts.total}
                      maxCount={99}
                      increaseContrast
                    />
                  )
                }
              >
                Threads
              </TabButton>
            </FilterContainer>

            {(() => {
              if (!revealed) {
                return (
                  <TabContainer>
                    <Skeleton flexGrow={1} />
                  </TabContainer>
                );
              }

              switch (tab) {
                case 'event-log': {
                  return (
                    <EventLogTab
                      entityIds={entityIds}
                      eventLogTypes={eventLogTypes}
                      onThreadAction={() => {
                        onThreadAction?.();
                        threadCountsQuery.refetch();
                      }}
                    />
                  );
                }
                case 'threads': {
                  return (
                    <ThreadsTab
                      domain={entityThreadDomain}
                      domainIdentifier={domainIdentifier}
                      onThreadAction={() => {
                        onThreadAction?.();
                        threadCountsQuery.refetch();
                      }}
                      counts={counts}
                    />
                  );
                }
                default:
                  return finleyAssertUnreachable(tab);
              }
            })()}

            {domainIdentifier && onCreateThread && (
              <EventLogNewThreadEditor
                externalOrgOptions={externalOrgOptions}
                onCreateThread={async (createThreadArgs) => {
                  // Some consumers of <EventLogDrawer /> need to run GQL mutations to
                  // "register" a thread with an entity. Awaiting on the consumer-provided
                  // callback here before refetching threads gives us a way to defer refetch until
                  // all consumer mutations are resoled.
                  await onCreateThread(createThreadArgs);
                }}
              />
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

// Exports
export const EventLogDrawer = ({
  triggerButtonProps,
  ...eventLogProps
}: EventLogDrawerProps) => {
  return (
    <ErrorBoundary
      fallback={({ showErrorToast, message }) => {
        showErrorToast(message);
        return (
          <IconButtonV2
            isDisabled
            icon={<Icon icon="bell" />}
            placement="bottom-end"
            {...triggerButtonProps}
            variant={triggerButtonProps?.variant ?? 'primary-muted'}
            aria-label={triggerButtonProps?.['aria-label'] ?? 'Activity'}
          />
        );
      }}
    >
      <EventLogDrawerBase
        triggerButtonProps={triggerButtonProps}
        {...eventLogProps}
      />
    </ErrorBoundary>
  );
};
